import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { OptionsCardSelect } from 'src/app/core/interfaces/interfaces';
import { SnackBarService } from 'src/app/core/services/utils/snackBar.service';
import { FechasService } from '../../../services/utils/fechas.service';
import { DialogService } from '../../../services/utils/dialog.service';
import { TerminationService } from '../../../services/modules/termination-service';
import { PersonalService } from '../../../services/modules/personal.service';

export type MassiveUploadTab = 'upload' | 'reports';

export interface DialogData {
  optionsCardSelect: OptionsCardSelect;
  title: string;
  data: any[];
  disableClose: boolean;
  btnClose: boolean;
  btnConfirm: boolean;
  textClose: string;
  textConfirm: string;
  autoClose: boolean;
  message: string;
  colorMessage: string;

  /**
   * default        => diseño actual usado por otros módulos
   * massiveUpload => diseño tipo cards para cargue masivo
   */
  viewMode?: 'default' | 'massiveUpload';
}

@Component({
  selector: 'app-dialog-selected-control-search-list',
  templateUrl: './dialog-selected-control-search-list.component.html',
  styleUrls: ['./dialog-selected-control-search-list.component.scss']
})
export class DialogSelectedControlSearchListComponent implements OnInit {

  controlSelected = new FormControl();
  datasource: any[] = [];
  selectedOption: any = null;

  // Vista nueva de cargue masivo: pestaña activa y paso dentro de "Actualizaciones e informes"
  activeTab: MassiveUploadTab = 'upload';
  reportsView: 'operations' | 'uploads' = 'operations';
  reportsOperation: any = null;
  reportsLoading = false;
  reportsUploads: any[] = [];

  constructor(
    public dialogRef: MatDialogRef<DialogSelectedControlSearchListComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private snackBService: SnackBarService,
    public fechasService: FechasService,
    private dialogService: DialogService,
    private terminationService: TerminationService,
    private personalService: PersonalService
  ) {
    if (this.data?.data !== null && this.data?.data?.length !== 0) {
      this.datasource = this.data.data;
    }
  }

  ngOnInit(): void { }

  get isMassiveUpload(): boolean {
    return this.data?.viewMode === 'massiveUpload';
  }

  get hasSelection(): boolean {
    const value = this.controlSelected.value;
    return value !== null && value !== undefined && value !== '';
  }

  close(): void {
    this.dialogRef.close(false);
  }

  save(): void {
    if (this.hasSelection) {
      this.dialogRef.close(this.controlSelected.value);
      return;
    }

    this.snackBService.openWarning(
      'Debes seleccionar alguna opción para continuar.',
      '',
      4000
    );
  }

  /**
   * Método usado por la vista antigua con app-select-search.
   */
  selected(e: any): void {
    if (e !== null && e !== undefined) {
      this.controlSelected.setValue(e, { emitEvent: false });
    }

    if (this.data?.autoClose === undefined) {
      this.dialogRef.close(e);
    }
  }

  /**
   * Método usado únicamente por la vista nueva de cargue masivo.
   */
  chooseOption(item: any): void {
    const value = this.getOptionValue(item);
    this.selectedOption = item;
    this.controlSelected.setValue(value);
    this.dialogRef.close(value);
  }

  selectOption(item: any): void {
    const value = this.getOptionValue(item);
    this.selectedOption = item;
    this.controlSelected.setValue(value);
  }

  setTab(tab: MassiveUploadTab): void {
    this.activeTab = tab;
    this.reportsView = 'operations';
  }

  /** Paso 1 -> 2: carga los cargues realizados de la operación elegida. */
  viewUpdates(item: any): void {
    const typeId = this.getOptionValue(item);

    this.reportsOperation = item;
    this.reportsUploads = [];
    this.reportsView = 'uploads';
    this.reportsLoading = true;

    // El historial se consulta por el Id del cargue (Info/{tipo}), no por el Id del tipo.
    this.personalService.getMassUploadInfoInt(typeId).subscribe((info: any) => {
      const massUploadId = info?.Id ? Number(info.Id) : null;
      if (!massUploadId) {
        this.reportsLoading = false;
        return;
      }
      this.terminationService.getHistoryGenerateWithdrawals(massUploadId).subscribe((history: any[]) => {
        this.reportsUploads = history || [];
        this.reportsLoading = false;
      }, () => this.handleUpdatesError());
    }, () => this.handleUpdatesError());
  }

  backToOperations(): void {
    this.reportsView = 'operations';
    this.reportsOperation = null;
    this.reportsUploads = [];
  }

  /** Paso 2 -> 3: el informe se abre encima y al cerrarlo se vuelve a esta lista. */
  viewReport(upload: any): void {
    const terminationDocumentId = Number(upload?.TerminationDocumentId);

    if (!terminationDocumentId) {
      this.snackBService.openWarning('No se encontró el documento para consultar el informe.', 'X', 3000);
      return;
    }

    this.dialogService.openDialogGenerateWithdrawalsReportComponent(terminationDocumentId, this.getUploadFileName(upload));
  }

  getUploadDate(upload: any): string {
    const date = new Date(upload?.CreateDate);
    if (!upload?.CreateDate || isNaN(date.getTime())) {
      return 'Fecha no reportada';
    }

    const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${date.getDate().toString().padStart(2, '0')} ${months[date.getMonth()]} ${date.getFullYear()} · ${hours}:${minutes}`;
  }

  getUploadFileName(upload: any): string {
    if (!upload?.InfoDocJson) {
      return '';
    }

    try {
      const parsed = JSON.parse(upload.InfoDocJson);
      const firstKey = Object.keys(parsed)[0];
      const parts: string[] = (parsed[firstKey] || '').split('||');
      const namePart = parts.find((part: string) => part.startsWith('name='));
      return namePart ? namePart.replace('name=', '') : (parts[0] || '').split('/').pop() || '';
    } catch {
      return '';
    }
  }

  getUploadStatusClass(upload: any): string {
    switch ((upload?.Status || '').toUpperCase()) {
      case 'COMPLETADO': return 'completed';
      case 'ERROR': return 'error';
      default: return 'started';
    }
  }

  getUploadStatusIcon(upload: any): string {
    switch (this.getUploadStatusClass(upload)) {
      case 'completed': return 'check_circle';
      case 'error': return 'error';
      default: return 'hourglass_top';
    }
  }

  private handleUpdatesError(): void {
    this.reportsLoading = false;
    this.snackBService.openWarning('Error al obtener el historial de actualizaciones.', 'X', 3000);
  }

  /** Solo "Generar retiros" tiene informe por cargue. */
  isReportDisabled(item: any): boolean {
    const title = (this.getOptionTitle(item) || '').toUpperCase();
    return title !== 'GENERAR RETIROS';
  }

  isOptionSelected(item: any): boolean {
    const optionValue = this.getOptionValue(item);
    return this.controlSelected.value === optionValue;
  }

  hasOptionImage(item: any): boolean {
    return this.getOptionImageSrc(item) !== null;
  }

  getOptionImageSrc(item: any): string | null {
    const key = (item?.key || item?.value || item?.type || item?.code || '')
      .toString()
      .toLowerCase();

    const imageByKey: { [key: string]: string } = {
      generar_retiros: 'assets/images/Salida.png',
    };

    if (imageByKey[key]) {
      return imageByKey[key];
    }

    // mismo fallback que getOptionIcon
    const title = (this.getOptionTitle(item) || '').toLowerCase();
    if (title.includes('retiro')) {
      return 'assets/images/Salida.png';
    }

    return null;
  }

  getOptionValue(item: any): any {
    const dataValue = this.data?.optionsCardSelect?.dataValue;
    const itemValue = this.data?.optionsCardSelect?.card?.itemValue;

    if (dataValue) {
      return this.getValueByPath(item, dataValue);
    }

    if (itemValue) {
      return this.getValueByPath(item, itemValue);
    }

    return item;
  }

  getOptionTitle(item: any): string {
    const cardTitle = this.data?.optionsCardSelect?.card?.title;
    const dataShow = this.data?.optionsCardSelect?.dataShow;

    const title =
      this.getValueByPath(item, cardTitle) ||
      this.getValueByPath(item, dataShow) ||
      item?.title ||
      item?.name ||
      item?.nombre ||
      item?.label ||
      item?.description ||
      item?.descripcion ||
      'Opción';

    return title;
  }

  getOptionSubtitle(item: any): string {
    const cardSubTitle = this.data?.optionsCardSelect?.card?.subTitle;

    const subtitle =
      this.getValueByPath(item, cardSubTitle) ||
      item?.subTitle ||
      item?.subtitle ||
      item?.description ||
      item?.descripcion ||
      item?.detalle ||
      item?.detail ||
      '';

    return subtitle;
  }

  getOptionIcon(item: any): string {
    if (item?.icon) {
      return item.icon;
    }

    const iconByKey: { [key: string]: string } = {
      // personal-list
      creacion_personal:   'group_add',
      documento_requisito: 'description',
      fecha_requisito:     'event',
      // terminations-list
      cambio_estado:       'swap_horiz',
      generar_retiros:     'person_remove',
      // común a ambos
      seguridad_social:    'health_and_safety',
    };

    const key = (item?.key || item?.value || item?.type || item?.code || '')
      .toString()
      .toLowerCase();

    if (iconByKey[key]) {
      return iconByKey[key];
    }

    const title = (this.getOptionTitle(item) || '').toLowerCase();

    if (title.includes('personal')) {         return 'group_add'; }
    if (title.includes('documento')) {        return 'description'; }
    if (title.includes('fecha')) {            return 'event'; }
    if (title.includes('cambio de estado')) { return 'swap_horiz'; }
    if (title.includes('retiro')) {           return 'person_remove'; }
    if (title.includes('seguridad social')) { return 'health_and_safety'; }
    return 'upload_file';
  }

  getValueByPath(item: any, path: any): any {
    if (!item || !path) {
      return null;
    }

    if (Array.isArray(path)) {
      return path
        .map((key) => this.getValueByPath(item, key))
        .filter((value) => value !== null && value !== undefined && value !== '')
        .join(' ');
    }

    if (typeof path !== 'string') {
      return null;
    }

    return path
      .split('.')
      .reduce((previousValue, currentKey) => previousValue?.[currentKey], item);
  }

  trackByOption(index: number, item: any): any {
    return item?.id || item?.value || item?.code || item?.codigo || index;
  }

}
